<template>
  <section class="p-8 max-mobile:py-4 max-mobile:px-2 scroll-mt-28">
    <div v-if="!isActivated">
      <RegistrationStepHeading
        :headline="`4. ${$t('dbc-headline-activation')}`"
        :showChangeButton="false"
      />

      <p class="font-semibold">
        {{ $t('dbc-text-verify-account') }}
      </p>
      {{ $t('dbc-activation-code-sent') }}:
      <span class="font-bold">{{
        verificationMedium?.value === 'E-Mail'
          ? personalData?.email
          : personalData?.phoneNumber
      }}</span>

      <DbcBanner
        class="w-fit my-xs max-mobile:w-full"
        :severity="'info'"
        :message="$t('dbc-activation-time-window')"
      />

      <div class="font-bold">
        {{ $t('dbc-activation-enter-code') }}
      </div>

      <RegistrationActivationCode @complete="onComplete" class="my-2" />

      <div v-if="activationError" class="text-attention-high my-4">
        {{ activationError }}
      </div>

      <div class="mt-8 text-sm">{{ $t('dbc-error-didnt-get-a-code') }}</div>
      <ul class="m-1 text-sm pl-4">
        <li>{{ $t('dbc-text-activation-help-1') }}</li>
        <li>{{ $t('dbc-text-activation-help-2') }}</li>
      </ul>
    </div>

    <h3 v-if="isActivated" class="dbc-gt-h3">
      {{ $t('dbc-headline-activation-complete') }}
    </h3>

    <LoadingOverlay :isLoading="isLoading" />
  </section>
</template>

<script setup lang="ts">
const props = defineProps<{
  verificationMedium?: SimpleAttribute
  personalData?: RegistrationPersonalData
  tariffName?: string
}>()

onMounted(() => {
  if (process.client) {
    window.scrollTo(0, 1)
  }
})

const { t, locale } = useI18n()
const successLandingPage = inject('successLandingPage') as string
const mobileOs = useMobileOs()

const runtimeConfig = useRuntimeConfig()
const client = runtimeConfig.public.client
const route = useRoute()

const isActivated = ref(false)
const activationError = ref('')
const isLoading = ref(false)

async function onComplete(activationCode: string) {
  isLoading.value = true

  const data: ActivationData = {
    activationCode: activationCode,
    ...(props.verificationMedium?.value !== 'E-Mail' && {
      phoneNumber: props.personalData?.phoneNumber,
    }),
    ...(props.verificationMedium?.value === 'E-Mail' && {
      email: props.personalData?.email,
    }),
    client: client,
    language: locale.value,
  }
  if (route.query.gclid) {
    data.gclid = route.query.gclid as string
    data.tariffName = `${client} ${props.tariffName}`
  }

  const response = await fetchCustomerActivate(data)

  isLoading.value = false
  if (response.status === 202) {
    isActivated.value = true

    trackEvent('Aktivierung', 'erfolgreich')

    const refreshToken = await fetchAppRefreshToken({
      email: props.personalData?.email as string,
      password: props.personalData?.password as string,
      locale: locale.value,
    })
    postWebviewMessage('registrationSuccess', {
      refreshToken: refreshToken,
    })
    if (successLandingPage && mobileOs.value !== '') {
      setTimeout(() => window.location.replace(successLandingPage), 5000)
    } else if (successLandingPage) {
      window.location.replace(successLandingPage)
    }
  }
  if (!response.ok) {
    isActivated.value = false
    activationError.value = response.data.message
    trackEvent('Aktivierung', 'fehlerhaft')
    postWebviewMessage('registrationFailure', {
      error: t('dbc-error-activation'),
    })
  }
}
</script>
